import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { Plus, Terminal, Database, Globe } from "lucide-react";
import { useDispatch } from "react-redux";
import { fetchAllServers } from "../redux/actionCreator";

const blankServer = {
  _id: null,
  label: "",
  host: "",
  port: 22,
  username: "",
  auth_type: "password",
  password: "",
  private_key: "",
  passphrase: "",
  base_path: "",
  use_sudo: false,
  docker_use_sudo: false,
  auto_sudo_shell: false,
  sudo_command: "sudo -i",
  sudo_password: "",        // new input only; empty = no change
  has_sudo_password: false, // server tells us if one is stored
  clear_sudo_password: false,
  k8s_use_sudo: false,
  k8s_namespace: "",
  k8s_namespaces: { development: "", staging: "", production: "" },
  nginx_enabled: false,
  nginx_conf_dir: "/etc/nginx",
  nginx_sites_dir: "/etc/nginx/conf.d",
  nginx_cf_creds_path: "/root/.secrets/cf.ini",
  nginx_ssl_email: "",
  mongo_enabled: false,
  mongo_uri: "", // plaintext on the way in; never returned by the API
  has_mongo_uri: false,
  clear_mongo_uri: false,
};

/**
 * Controlled component — the parent owns the `value` array and `onChange`
 * gets a fresh array on every add/edit/remove. Secrets stay in local state
 * here and are flushed to the server only when the parent submits the
 * project form.
 *
 * For existing servers loaded from the API, `_id` is set but the secret
 * fields are blank (the API never returns secrets). Sending the entry back
 * with blank secret means "keep the encrypted value on the server".
 */
export default function ServersPanel({ value = [], onChange }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // index in `value` being edited, or null for new
  const [data, setData] = useState(blankServer);
  const [err, setErr] = useState({});
  const [showPicker, setShowPicker] = useState(false);

  // Called by the picker when the operator selects an existing server.
  // We strip the source `_id` (so Mongoose mints a fresh one inside this
  // project's servers array) and the `project_id`/`project_name` context,
  // then push the rest as-is. Encrypted credential blobs (password / key
  // / passphrase / sudo / mongo URI) round-trip unchanged because they're
  // sealed with the same env-level key.
  const cloneFromExisting = (src) => {
    const {
      _id, project_id, project_name, has_sudo_password, has_mongo_uri,
      ...rest
    } = src || {};
    onChange([
      ...value,
      {
        ...rest,
        // Add a tiny disambiguator if a server with the same label is
        // already on this project — keeps the picker idempotent.
        label: value.some((v) => (v.label || "").toLowerCase() === (rest.label || "").toLowerCase())
          ? `${rest.label} (copy)`
          : rest.label,
      },
    ]);
    setShowPicker(false);
  };

  const openCreate = () => {
    setEditing(null);
    setData(blankServer);
    setErr({});
    setShowForm(true);
  };

  const openEdit = (idx) => {
    const s = value[idx];
    setEditing(idx);
    setData({
      _id: s._id || null,
      label: s.label || "",
      host: s.host || "",
      port: s.port || 22,
      username: s.username || "",
      auth_type: s.auth_type || "password",
      password: s.password || "",
      private_key: s.private_key || "",
      passphrase: s.passphrase || "",
      base_path: s.base_path || "",
      use_sudo: !!s.use_sudo,
      docker_use_sudo: !!s.docker_use_sudo,
      auto_sudo_shell: !!s.auto_sudo_shell,
      sudo_command: s.sudo_command || "sudo -i",
      sudo_password: "",
      has_sudo_password: !!s.has_sudo_password,
      clear_sudo_password: false,
      k8s_use_sudo: !!s.k8s_use_sudo,
      k8s_namespace: s.k8s_namespace || "",
      k8s_namespaces: {
        development: s.k8s_namespaces?.development || "",
        staging:     s.k8s_namespaces?.staging || "",
        production:  s.k8s_namespaces?.production || "",
      },
      nginx_enabled: !!s.nginx_enabled,
      nginx_conf_dir: s.nginx_conf_dir || "/etc/nginx",
      nginx_sites_dir: s.nginx_sites_dir || "/etc/nginx/conf.d",
      nginx_cf_creds_path: s.nginx_cf_creds_path || "/root/.secrets/cf.ini",
      nginx_ssl_email: s.nginx_ssl_email || "",
      mongo_enabled: !!s.mongo_enabled,
      mongo_uri: "",
      has_mongo_uri: !!s.has_mongo_uri,
      clear_mongo_uri: false,
    });
    setErr({});
    setShowForm(true);
  };

  const onChangeField = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErr((e) => ({ ...e, [k]: "" }));
  };

  const validate = () => {
    const t = {};
    if (!data.label?.trim()) t.label = "Required";
    if (!data.host?.trim()) t.host = "Required";
    if (!data.username?.trim()) t.username = "Required";
    if (!Number(data.port) || data.port < 1 || data.port > 65535)
      t.port = "1–65535";
    // Secret is required when adding a new server. For existing servers,
    // blank = keep the encrypted value already on the backend.
    const existing = editing != null && value[editing]?._id;
    if (!existing) {
      if (data.auth_type === "password" && !data.password)
        t.password = "Required";
      if (data.auth_type === "key" && !data.private_key)
        t.private_key = "Required";
    }
    setErr(t);
    return Object.keys(t).length === 0;
  };

  const save = () => {
    if (!validate()) return;
    const entry = {
      _id: data._id,
      label: data.label.trim(),
      host: data.host.trim(),
      port: Number(data.port),
      username: data.username.trim(),
      auth_type: data.auth_type,
      base_path: data.base_path.trim(),
      use_sudo: !!data.use_sudo,
      docker_use_sudo: !!data.docker_use_sudo,
      auto_sudo_shell: !!data.auto_sudo_shell,
      sudo_command: (data.sudo_command || "sudo -i").trim(),
      // Only send a new password if the user typed one. clear_sudo_password
      // wipes any stored value on the backend.
      ...(data.sudo_password ? { sudo_password: data.sudo_password } : {}),
      ...(data.clear_sudo_password ? { clear_sudo_password: true } : {}),
      k8s_use_sudo: !!data.k8s_use_sudo,
      k8s_namespace: (data.k8s_namespace || "").trim(),
      k8s_namespaces: {
        development: (data.k8s_namespaces?.development || "").trim(),
        staging:     (data.k8s_namespaces?.staging || "").trim(),
        production:  (data.k8s_namespaces?.production || "").trim(),
      },
      nginx_enabled: !!data.nginx_enabled,
      nginx_conf_dir: (data.nginx_conf_dir || "").trim() || "/etc/nginx",
      nginx_sites_dir: (data.nginx_sites_dir || "").trim() || "/etc/nginx/conf.d",
      nginx_cf_creds_path: (data.nginx_cf_creds_path || "").trim(),
      nginx_ssl_email: (data.nginx_ssl_email || "").trim(),
      mongo_enabled: !!data.mongo_enabled,
      // Only send a new URI if the user typed one; otherwise the saved
      // encrypted value stays untouched. `clear_mongo_uri` wipes it.
      ...(data.mongo_uri ? { mongo_uri: data.mongo_uri } : {}),
      ...(data.clear_mongo_uri ? { clear_mongo_uri: true } : {}),
    };
    if (data.password) entry.password = data.password;
    if (data.private_key) entry.private_key = data.private_key;
    if (data.passphrase !== "") entry.passphrase = data.passphrase;

    const next = [...value];
    if (editing != null) {
      next[editing] = { ...next[editing], ...entry };
    } else {
      next.push(entry);
    }
    onChange(next);
    setShowForm(false);
  };

  const remove = (idx) => {
    const s = value[idx];
    if (!window.confirm(`Remove ${s.label || "this server"}?`)) return;
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <h6 className="form-section-title mb-0" style={{ border: 0 }}>
          Servers
          <span className="form-section-hint">
            Encrypted at rest · members can open a terminal
          </span>
        </h6>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-primary" onClick={() => setShowPicker(true)}>
            <Plus size={14} className="me-1" /> Pick existing
          </Button>
          <Button size="sm" variant="primary" onClick={openCreate}>
            <Plus size={14} className="me-1" /> Add Server
          </Button>
        </div>
      </div>

      {value.length === 0 ? (
        <div className="text-muted small mt-2">No servers yet.</div>
      ) : (
        <table className="table table-sm align-middle mb-0 mt-2">
          <thead>
            <tr>
              <th>Label</th>
              <th>Host</th>
              <th style={{ width: 64 }}>Port</th>
              <th>User</th>
              <th>Auth</th>
              <th className="text-end" style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {value.map((s, i) => {
              const isNew = !s._id;
              const hasSecretChange =
                !!s.password || !!s.private_key || s.passphrase !== undefined;
              return (
                <tr key={s._id || `new-${i}`}>
                  <td>
                    {s.label}
                    {isNew && (
                      <span className="badge bg-info ms-2">unsaved</span>
                    )}
                    {!isNew && hasSecretChange && (
                      <span className="badge bg-warning text-dark ms-2">
                        secret edited
                      </span>
                    )}
                  </td>
                  <td>
                    <code>{s.host}</code>
                  </td>
                  <td>{s.port}</td>
                  <td>{s.username}</td>
                  <td>
                    <span className="badge bg-secondary">
                      {s.auth_type === "key" ? "Key" : "Password"}
                    </span>
                    {s.use_sudo && (
                      <span
                        className="badge bg-warning text-dark ms-1"
                        title="Privileged shell ops via sudo"
                      >
                        sudo
                      </span>
                    )}
                  </td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => openEdit(i)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="link"
                      className="text-danger"
                      onClick={() => remove(i)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        centered
        size="lg"
        dialogClassName="sh-form-modal"
      >
        <Modal.Header closeButton>
          <span className="sh-modal-icon"><Terminal size={22} /></span>
          <div className="sh-modal-titles">
            <h4>{editing != null ? "Edit Server" : "Add Server"}</h4>
            <span className="sh-modal-sub">
              SSH credentials are encrypted at rest before saving
            </span>
          </div>
        </Modal.Header>
        <Modal.Body>
          {/* ── Connection ── */}
          <div className="form-section">
            <h6 className="form-section-title">Connection</h6>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Label *</Form.Label>
                  <Form.Control
                    value={data.label}
                    onChange={(e) => onChangeField("label", e.target.value)}
                    placeholder="Production API"
                  />
                  <span className="text-danger">{err.label}</span>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Host / IP *</Form.Label>
                  <Form.Control
                    value={data.host}
                    onChange={(e) => onChangeField("host", e.target.value)}
                    placeholder="10.0.0.21"
                  />
                  <span className="text-danger">{err.host}</span>
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-0">
                  <Form.Label>Port *</Form.Label>
                  <Form.Control
                    type="number"
                    value={data.port}
                    onChange={(e) => onChangeField("port", e.target.value)}
                  />
                  <span className="text-danger">{err.port}</span>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-0">
                  <Form.Label>SSH Username *</Form.Label>
                  <Form.Control
                    value={data.username}
                    onChange={(e) => onChangeField("username", e.target.value)}
                    placeholder="deploy"
                  />
                  <span className="text-danger">{err.username}</span>
                </Form.Group>
              </div>
              <div className="col-md-5">
                <Form.Group className="mb-0">
                  <Form.Label>Auth method *</Form.Label>
                  <Form.Select
                    value={data.auth_type}
                    onChange={(e) => onChangeField("auth_type", e.target.value)}
                  >
                    <option value="password">Password</option>
                    <option value="key">Private key</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </div>

          {/* ── Credentials ── */}
          <div className="form-section">
            <h6 className="form-section-title">Credentials</h6>
            {data.auth_type === "password" ? (
              <Form.Group className="mb-0">
                <Form.Label>
                  Password{" "}
                  {data._id ? "(leave blank to keep current)" : "*"}
                </Form.Label>
                <Form.Control
                  type="password"
                  autoComplete="new-password"
                  value={data.password}
                  onChange={(e) => onChangeField("password", e.target.value)}
                />
                <span className="text-danger">{err.password}</span>
              </Form.Group>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Private key (PEM){" "}
                    {data._id ? "(leave blank to keep current)" : "*"}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={data.private_key}
                    onChange={(e) =>
                      onChangeField("private_key", e.target.value)
                    }
                    placeholder={"-----BEGIN OPENSSH PRIVATE KEY-----\n..."}
                    style={{
                      fontFamily: "ui-monospace, monospace",
                      fontSize: 12,
                    }}
                  />
                  <span className="text-danger">{err.private_key}</span>
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label>
                    Passphrase{" "}
                    <small className="text-muted">
                      (only if the key is encrypted)
                    </small>
                  </Form.Label>
                  <Form.Control
                    type="password"
                    autoComplete="new-password"
                    value={data.passphrase}
                    onChange={(e) =>
                      onChangeField("passphrase", e.target.value)
                    }
                  />
                </Form.Group>
              </>
            )}
          </div>

          {/* ── File manager ── */}
          <div className="form-section">
            <h6 className="form-section-title">
              File manager
              <span className="form-section-hint">optional</span>
            </h6>
            <Form.Group className="mb-0">
              <Form.Label>File-manager root</Form.Label>
              <Form.Control
                value={data.base_path}
                onChange={(e) => onChangeField("base_path", e.target.value)}
                placeholder="/var/www/myapp"
                style={{ fontFamily: "ui-monospace, monospace" }}
              />
              <small className="text-muted">
                Path on the remote that the in-portal file manager is
                jailed to. Leave blank to disable file manager for this
                server. Employees can list, upload, edit, delete, zip and
                unzip inside this folder — never outside it.
              </small>
            </Form.Group>
          </div>

          {/* ── Privileges & sudo ── */}
          <div className="form-section">
            <h6 className="form-section-title">Privileges &amp; sudo</h6>
            <Form.Group className="form-switch-row">
              <Form.Check
                type="switch"
                id="use-sudo-toggle"
                checked={!!data.use_sudo}
                onChange={(e) => onChangeField("use_sudo", e.target.checked)}
                label="Run privileged shell ops via sudo (rm / cp / zip / unzip)"
              />
              <small className="text-muted">
                When on, the file manager prefixes shell commands with{" "}
                <code>sudo -n</code> so the SSH user can act on root-owned
                files. The SSH user must have a <code>NOPASSWD</code> sudoers
                entry for the commands it runs (see deploy notes). Upload,
                edit, mkdir, chmod still go over SFTP as the SSH user and
                are not elevated.
              </small>
            </Form.Group>
            <Form.Group className="form-switch-row">
              <Form.Check
                type="switch"
                id="docker-use-sudo-toggle"
                checked={!!data.docker_use_sudo}
                onChange={(e) => onChangeField("docker_use_sudo", e.target.checked)}
                label="Run docker via sudo (use when the SSH user isn't in the docker group)"
              />
              <small className="text-muted">
                When on, every docker command (including <code>docker exec</code> for in-container terminals)
                is prefixed with <code>sudo -n docker</code>. Requires a sudoers entry on the remote, e.g.:{" "}
                <code>{`<user> ALL=(root) NOPASSWD: /usr/bin/docker`}</code>
              </small>
            </Form.Group>
            <Form.Group className="form-switch-row">
              <Form.Check
                type="switch"
                id="k8s-use-sudo-toggle"
                checked={!!data.k8s_use_sudo}
                onChange={(e) => onChangeField("k8s_use_sudo", e.target.checked)}
                label="Run kubectl via sudo"
              />
              <small className="text-muted d-block mb-2">
                Most setups don't need this — kubectl reads <code>~/.kube/config</code> of the SSH user.
                Turn on if your cluster credentials are only readable by root.
              </small>
              <Form.Label className="small mb-1">
                Default Kubernetes namespace{" "}
                <small className="text-muted">(optional — fallback when no per-stage namespace is set)</small>
              </Form.Label>
              <Form.Control
                value={data.k8s_namespace}
                onChange={(e) => onChangeField("k8s_namespace", e.target.value)}
                placeholder="e.g. default — used when the per-stage map below is blank"
                style={{ fontFamily: "ui-monospace, monospace" }}
                maxLength={253}
              />
              <div className="mt-2 p-2 rounded" style={{ background: "#f8fafc", border: "1px solid #e5e7eb" }}>
                <Form.Label className="small mb-1">
                  Per-stage namespaces{" "}
                  <small className="text-muted">
                    — when one physical cluster serves all three stages, give each its own namespace
                  </small>
                </Form.Label>
                {[
                  { key: "development", label: "Development", color: "#2563eb" },
                  { key: "staging",     label: "Staging",     color: "#b45309" },
                  { key: "production",  label: "Production",  color: "#b91c1c" },
                ].map(({ key, label, color }) => (
                  <div key={key} className="d-flex align-items-center gap-2 mb-1">
                    <span
                      className="badge"
                      style={{
                        background: "#fff",
                        color,
                        border: `1px solid ${color}`,
                        fontWeight: 600,
                        minWidth: 110,
                        textAlign: "center",
                      }}
                    >
                      {label}
                    </span>
                    <Form.Control
                      size="sm"
                      value={data.k8s_namespaces?.[key] || ""}
                      onChange={(e) =>
                        onChangeField("k8s_namespaces", {
                          ...(data.k8s_namespaces || {}),
                          [key]: e.target.value,
                        })
                      }
                      placeholder={`e.g. ${label.toLowerCase()} namespace, blank = use default`}
                      style={{ fontFamily: "ui-monospace, monospace" }}
                      maxLength={253}
                    />
                  </div>
                ))}
              </div>
            </Form.Group>
          </div>

          {/* ── NGINX ── */}
          <div className="form-section">
            <h6 className="form-section-title">NGINX</h6>
            <Form.Group className="form-switch-row">
              <Form.Check
                type="switch"
                id="nginx-enabled-toggle"
                checked={!!data.nginx_enabled}
                onChange={(e) => onChangeField("nginx_enabled", e.target.checked)}
                label="Enable NGINX config management"
              />
              <small className="text-muted d-block mb-2">
                Surfaces an <strong>NGINX</strong> button on this server. Reads/writes
                go through <code>sudo</code> (with the stored sudo password if any).
              </small>
              {data.nginx_enabled && (
                <div className="row g-2 mt-1">
                  <div className="col-md-6">
                    <Form.Label className="small mb-1">nginx config root</Form.Label>
                    <Form.Control
                      value={data.nginx_conf_dir}
                      onChange={(e) => onChangeField("nginx_conf_dir", e.target.value)}
                      placeholder="/etc/nginx"
                      style={{ fontFamily: "ui-monospace, monospace" }}
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Label className="small mb-1">
                      vhosts dir(s){" "}
                      <small className="text-muted">comma-separated</small>
                    </Form.Label>
                    <Form.Control
                      value={data.nginx_sites_dir}
                      onChange={(e) => onChangeField("nginx_sites_dir", e.target.value)}
                      placeholder="/etc/nginx/conf.d, /etc/nginx/sites-available"
                      style={{ fontFamily: "ui-monospace, monospace" }}
                    />
                    <small className="text-muted">
                      Scans every listed dir. Add-Subdomain writes into the first.
                    </small>
                  </div>
                  <div className="col-md-6">
                    <Form.Label className="small mb-1">
                      Cloudflare credentials INI (on remote)
                    </Form.Label>
                    <Form.Control
                      value={data.nginx_cf_creds_path}
                      onChange={(e) => onChangeField("nginx_cf_creds_path", e.target.value)}
                      placeholder="/root/.secrets/cf.ini"
                      style={{ fontFamily: "ui-monospace, monospace" }}
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Label className="small mb-1">
                      Let's Encrypt registration email
                    </Form.Label>
                    <Form.Control
                      type="email"
                      value={data.nginx_ssl_email}
                      onChange={(e) => onChangeField("nginx_ssl_email", e.target.value)}
                      placeholder="ops@smarthatch.com"
                    />
                  </div>
                  <div className="col-md-12">
                    <small className="text-muted">
                      The Cloudflare INI is read by certbot — needs at minimum:{" "}
                      <code>dns_cloudflare_api_token = …</code>. Put it on the remote
                      host, owned <code>root:root</code>, mode <code>0600</code>.
                    </small>
                  </div>
                </div>
              )}
            </Form.Group>
          </div>

          {/* ── MongoDB ── */}
          <div className="form-section">
            <h6 className="form-section-title">MongoDB</h6>
            <Form.Group className="form-switch-row">
              <Form.Check
                type="switch"
                id="mongo-enabled-toggle"
                checked={!!data.mongo_enabled}
                onChange={(e) => onChangeField("mongo_enabled", e.target.checked)}
                label="Enable Mongo management"
              />
              <small className="text-muted d-block mb-2">
                Adds a <strong className="d-inline-flex align-items-center gap-1"><Database size={12} /> Mongo</strong> button on this server. Lets you browse
                collections, take backups (<code>mongodump → download</code>), and
                restore (<code>upload → mongorestore</code>). Tools must be installed
                on the server: <code>mongosh</code>, <code>mongodump</code>,{" "}
                <code>mongorestore</code>.
              </small>
              {data.mongo_enabled && (
                <div className="row g-2 mt-1">
                  <div className="col-12">
                    <Form.Label className="small mb-1">
                      Connection URI{" "}
                      {data.has_mongo_uri && !data.mongo_uri && (
                        <span className="badge bg-success ms-1">stored</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={data.mongo_uri}
                      onChange={(e) => onChangeField("mongo_uri", e.target.value)}
                      placeholder={data.has_mongo_uri ? "•••••••• (saved — type to replace)" : "mongodb://user:pass@127.0.0.1:27017/dbname"}
                      style={{ fontFamily: "ui-monospace, monospace" }}
                      autoComplete="new-password"
                    />
                    <small className="text-muted">
                      AES-256-GCM encrypted at rest. The URI is decrypted only when running mongo commands — never logged or returned.
                    </small>
                  </div>
                  {data.has_mongo_uri && (
                    <div className="col-12">
                      <Form.Check
                        type="checkbox"
                        id="mongo-clear"
                        checked={!!data.clear_mongo_uri}
                        onChange={(e) => onChangeField("clear_mongo_uri", e.target.checked)}
                        label={<small className="text-danger">Clear stored URI on save</small>}
                      />
                    </div>
                  )}
                </div>
              )}
            </Form.Group>
          </div>

          {/* ── Terminal session ── */}
          <div className="form-section">
            <h6 className="form-section-title">Terminal session</h6>
            <Form.Group className="form-switch-row">
              <Form.Check
                type="switch"
                id="auto-sudo-shell-toggle"
                checked={!!data.auto_sudo_shell}
                onChange={(e) => onChangeField("auto_sudo_shell", e.target.checked)}
                label="Open terminal as sudo (root) by default"
              />
              <small className="text-muted">
                When on, the in-portal terminal runs the elevation command below right after connect — no need to type <code>sudo</code> for every command.
                The Files panel and container exec ignore this flag.
              </small>
              {data.auto_sudo_shell && (
                <div className="mt-2">
                  <Form.Label className="small mb-1">Elevation command</Form.Label>
                  <Form.Control
                    value={data.sudo_command || ""}
                    onChange={(e) => onChangeField("sudo_command", e.target.value)}
                    placeholder="sudo -i"
                    style={{ fontFamily: "ui-monospace, monospace" }}
                    maxLength={200}
                  />
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {["sudo -i", "sudo su -", "su -", "sudo -u root -s", "sudo -s"].map((preset) => (
                      <Button
                        key={preset}
                        size="sm"
                        variant="light"
                        className="py-0 px-2"
                        onClick={() => onChangeField("sudo_command", preset)}
                        style={{ fontSize: 11, fontFamily: "ui-monospace, monospace" }}
                      >
                        {preset}
                      </Button>
                    ))}
                  </div>
                  <small className="text-muted d-block mt-1">
                    Free-form. Common: <code>sudo -i</code> (NOPASSWD),{" "}
                    <code>sudo su -</code>, <code>su -</code> (needs root password — supply it below or it'll prompt in the terminal),{" "}
                    <code>sudo -u root -s</code>. Site-specific wrappers like <code>pbrun</code> also work.
                  </small>

                  <Form.Label className="small mb-1 mt-3">
                    Sudo / root password{" "}
                    <small className="text-muted">(optional)</small>
                  </Form.Label>
                  <Form.Control
                    type="password"
                    autoComplete="new-password"
                    value={data.sudo_password || ""}
                    onChange={(e) => onChangeField("sudo_password", e.target.value)}
                    placeholder={
                      data.has_sudo_password
                        ? "•••••••• stored — type to replace"
                        : "Leave blank to be prompted in the terminal"
                    }
                  />
                  <div className="d-flex justify-content-between align-items-start mt-1">
                    <small className="text-muted">
                      Stored encrypted (AES-256-GCM). The terminal swallows the prompt and supplies this password on connect.
                      Skip this if your sudo is <code>NOPASSWD</code>.
                    </small>
                    {data.has_sudo_password && (
                      <Form.Check
                        type="checkbox"
                        id="clear-sudo-pw"
                        label={<small>Clear</small>}
                        checked={!!data.clear_sudo_password}
                        onChange={(e) => onChangeField("clear_sudo_password", e.target.checked)}
                        className="ms-2"
                      />
                    )}
                  </div>
                </div>
              )}
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowForm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save}>
            {editing != null ? "Save Server" : "Add Server"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ExistingServerPicker
        show={showPicker}
        onClose={() => setShowPicker(false)}
        onPick={cloneFromExisting}
        alreadyAdded={value}
      />
    </>
  );
}

/**
 * Picks an existing server from any project the operator has access to.
 * Backend (`GET /admin/all-servers`) returns the full subdoc — including
 * encrypted credential blobs — so a clone-on-select reuses the same
 * SSH password / private key without forcing re-entry.
 *
 * Already-added servers (matched by source `_id`) are flagged so the
 * picker doesn't let you clone the same one twice.
 */
function ExistingServerPicker({ show, onClose, onPick, alreadyAdded = [] }) {
  const dispatch = useDispatch();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    dispatch(fetchAllServers()).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res?.status) setList(res.data || []);
      else setErr(res?.message || "Failed to load servers");
    });
    return () => { cancelled = true; };
  }, [show, dispatch]);

  const f = filter.trim().toLowerCase();
  const filtered = list.filter((s) => {
    if (!f) return true;
    return (
      (s.label || "").toLowerCase().includes(f) ||
      (s.host || "").toLowerCase().includes(f) ||
      (s.username || "").toLowerCase().includes(f) ||
      (s.project_name || "").toLowerCase().includes(f)
    );
  });

  // Source-server ID set already added to this project — by upstream id,
  // not the freshly-minted subdoc id (which is missing on the clone).
  const usedIds = new Set(
    alreadyAdded
      .map((s) => s?._clone_source_id)
      .filter(Boolean)
      .map(String),
  );

  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <h5 className="m-0">Pick an existing server</h5>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          autoFocus
          size="sm"
          placeholder="Filter by label, host, user, or project…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-2"
        />
        {err && <div className="alert alert-warning small">{err}</div>}
        {loading ? (
          <div className="text-center py-4"><Spinner size="sm" animation="border" /> Loading…</div>
        ) : !filtered.length ? (
          <div className="text-muted small py-3 text-center">
            {list.length === 0 ? "No servers in any project yet." : "No matches for the current filter."}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Server</th>
                  <th>Host</th>
                  <th>User</th>
                  <th>Auth</th>
                  <th style={{ width: 1 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const used = usedIds.has(String(s._id));
                  return (
                    <tr key={`${s.project_id}-${s._id}`}>
                      <td className="small">{s.project_name || "—"}</td>
                      <td>
                        <strong className="small">{s.label}</strong>
                        {(s.nginx_enabled || s.mongo_enabled) && (
                          <div className="small text-muted">
                            {s.nginx_enabled && <span className="me-1 d-inline-flex align-items-center gap-1"><Globe size={12} /> NGINX</span>}
                            {s.mongo_enabled && <span className="d-inline-flex align-items-center gap-1"><Database size={12} /> Mongo</span>}
                          </div>
                        )}
                      </td>
                      <td>
                        <code className="small">{s.host}:{s.port}</code>
                      </td>
                      <td className="small">{s.username}</td>
                      <td>
                        <span className={`badge ${s.auth_type === "key" ? "bg-secondary" : "bg-light text-dark"}`}>
                          {s.auth_type === "key" ? "Key" : "Password"}
                        </span>
                      </td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={used}
                          onClick={() => onPick({ ...s, _clone_source_id: s._id })}
                          title={used ? "Already added to this project" : "Clone into this project"}
                        >
                          {used ? "Added" : "Pick"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <small className="text-muted d-block mt-2">
          Picking copies the server's config (including encrypted password / key) into this project as a new entry.
          Edit afterwards to change anything for this project specifically.
        </small>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
